import React from 'react';
import Icon from '../../../components/AppIcon';
import { useLanguage } from '../../../contexts/LanguageContext';

const Footer = () => {
  const { t } = useLanguage();
  const currentYear = new Date()?.getFullYear();

  const footerLinks = {
    product: [
      { label: t('footer.features'), href: '#features' },
      { label: t('footer.howItWorks'), href: '#how-it-works' },
      { label: t('footer.forTalent'), href: '#talent' },
      { label: t('footer.forEmployers'), href: '#employers' }
    ],
    company: [
      { label: t('footer.aboutUs'), href: '#about' },
      { label: t('footer.contact'), href: '#contact' },
      { label: t('footer.blog'), href: '#blog' }
    ],
    legal: [
      { label: t('footer.privacyPolicy'), href: '#privacy' },
      { label: t('footer.termsOfService'), href: '#terms' },
      { label: t('footer.cookiePolicy'), href: '#cookies' },
      { label: t('footer.dataProtection'), href: '#data' }
    ]
  };

  const socialLinks = [
    { icon: 'Linkedin', href: 'https://www.linkedin.com/company/blind', label: 'LinkedIn' },
    { icon: 'Twitter', href: 'https://twitter.com/blind', label: 'Twitter' },
    { icon: 'Facebook', href: 'https://www.facebook.com/blind', label: 'Facebook' }
  ];

  const handleLinkClick = (e, href) => {
    if (href?.startsWith('#')) {
      e?.preventDefault();
      const element = document.querySelector(href);
      if (element) {
        element?.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  return (
    <footer className="bg-[#111827] text-white">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-12 md:py-16 lg:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 md:gap-12 mb-12 md:mb-16">
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3 mb-4 md:mb-6">
              <span className="text-2xl font-bold">BlinD</span>
            </div>
            <p className="text-sm md:text-base text-white/70 mb-6 leading-relaxed max-w-md">
              {t('footer.tagline')}
            </p>
            <div className="flex gap-4">
              {socialLinks?.map((social) => (
                <a
                  key={social?.label}
                  href={social?.href}
                  aria-label={social?.label}
                  className="w-10 h-10 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors duration-250"
                >
                  <Icon name={social?.icon} size={20} color="#FFFFFF" />
                </a>
              ))}
            </div>
          </div>
          
          <div>
            <h3 className="text-base md:text-lg font-semibold mb-4">{t('footer.product')}</h3>
            <ul className="space-y-3">
              {footerLinks?.product?.map((link) => (
                <li key={link?.label}>
                  <a
                    href={link?.href}
                    onClick={(e) => handleLinkClick(e, link?.href)}
                    className="text-sm md:text-base text-white/70 hover:text-white transition-colors duration-250"
                  >
                    {link?.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
          
          <div>
            <h3 className="text-base md:text-lg font-semibold mb-4">{t('footer.company')}</h3>
            <ul className="space-y-3">
              {footerLinks?.company?.map((link) => (
                <li key={link?.label}>
                  <a
                    href={link?.href}
                    onClick={(e) => handleLinkClick(e, link?.href)}
                    className="text-sm md:text-base text-white/70 hover:text-white transition-colors duration-250"
                  >
                    {link?.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
          
          <div>
            <h3 className="text-base md:text-lg font-semibold mb-4">{t('footer.legal')}</h3>
            <ul className="space-y-3">
              {footerLinks?.legal?.map((link) => (
                <li key={link?.label}>
                  <a
                    href={link?.href}
                    onClick={(e) => handleLinkClick(e, link?.href)}
                    className="text-sm md:text-base text-white/70 hover:text-white transition-colors duration-250"
                  >
                    {link?.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
        
        <div className="pt-8 border-t border-white/10">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-white/70 text-center md:text-left">
              &copy; {currentYear} {t('footer.allRightsReserved')}
            </p>
            <div className="flex items-center gap-6 text-sm text-white/70">
              <span className="flex items-center gap-2">
                <Icon name="MapPin" size={16} color="#FFFFFF" />
                {t('footer.location')}
              </span>
              <span className="flex items-center gap-2">
                <Icon name="Shield" size={16} color="#FFFFFF" />
                {t('footer.compliant')}
              </span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;